import {motion} from "framer-motion";
import {useNavigate, useParams} from "react-router-dom";
import navigationEffect from "../animations/navigationEffect";
import {useEffect, useState} from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Nav from "react-bootstrap/Nav";
import {SubInterface} from "./Sub";
import {CategoryProps} from "./Category";
import {LinkContainer} from "react-router-bootstrap";
import {MediaProp} from "../components/MediaProp";
import {CaretDown, CaretDownFill} from "react-bootstrap-icons";
import './Notebook.scss'
import {LessonInterface} from "./Lesson";
import fadeEffect from "../animations/fadeEffect";
import {SegmentViewer} from "../components/SegmentViewer";
import {apiCallbacks} from "../App";

export interface BookInterface extends SubInterface {
    categoryId: number;
    subId: number;
    segments: {
        id: number;
        title: string;
    }[];
}

export interface UrokInterface {
    id: number;
    title: string;
    page: string;
    lessons: LessonInterface[];
}

export interface SegmentInterface {
    id: number;
    uroks: UrokInterface[]
}

interface NotebookProps extends CategoryProps, apiCallbacks {
    book: string;
}

export const Notebook = ({book, requireAuthCallback, authCheckCatchCallback}: NotebookProps) => {
    const {category, sub} = useParams();
    const [bookData, setBookData] = useState<BookInterface>();
    const [segmentId, setSegmentId] = useState<number>();
    const [segmentDropdownActive, setSegmentDropdownActive] = useState(false)
    const navigate = useNavigate();
    const mediaProps = MediaProp.Query();

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API}/books/${book}/`, {credentials: "include"})
            .then(requireAuthCallback)
            .then((data) => {
                try {
                    if (data.detail === "Not found.") {
                        navigate(`/category/${category}/${sub}/change`);
                        console.log('Book not found');
                        return Promise.reject(data.detail);
                    }
                } catch (error) {}
                setBookData(data)
            })
            .then(() => {
                if (segmentId) {
                    document.getElementById(segmentId.toString())?.scrollIntoView({
                        behavior: 'smooth'
                    })
                }
            })
            .catch(authCheckCatchCallback)
            .catch(() => navigate(`/category/${category}/${sub}/change`));
    }, [category, sub, book, navigate, segmentId, requireAuthCallback, authCheckCatchCallback])

    if (!bookData) {
        return (
            <motion.div {...navigationEffect}>
                <Container className="text-center">
                    <h1>404 not found.</h1>
                </Container>
            </motion.div>
        )
    }

    return (
        <motion.div {...navigationEffect}>
            <Container className="shadow-sm mb-3 p-2 rounded-2 book-header">
                <Row>
                    <Col md="auto" className="my-2">
                        <img
                            src={bookData?.cover.startsWith('/') ? bookData?.cover : `/${bookData?.cover}`}
                            alt={bookData?.title}
                            className="rounded-circle object-fit-cover me-3"
                            width={50} height={50}/>
                        <LinkContainer to={`/category/${category}/${sub}/change`}>
                            <Button>Смени</Button>
                        </LinkContainer>
                    </Col>
                    <Col className="text-wrap my-2 h-100 align-self-center">
                        <h2 className="m-0">{bookData?.title.replaceAll('"', '')}</h2>
                    </Col>
                </Row>
            </Container>
            <div className={`d-flex w-100 ${mediaProps.mobile ? 'px-0' : 'px-5'} py-2 flex-column`}>
                {mediaProps.mobile &&
                    <Row className="mb-4"><Col>
                        <div className={`mt-2 px-4 border rounded-2 ${segmentDropdownActive ? 'rounded-bottom-0' : ''}`}
                             onClick={() => setSegmentDropdownActive(!segmentDropdownActive)}>
                            <div className="d-flex">
                                <h2 className="w-100">Раздели</h2>
                                <div className="my-auto">
                                    {segmentDropdownActive && <CaretDown size={25}/>}
                                    {!segmentDropdownActive && <CaretDownFill size={25}/>}
                                </div>
                            </div>
                        </div>
                        {segmentDropdownActive &&
                            <Nav
                                className="flex-column segment-navigator my-0 border border-top-0 rounded-2 rounded-top-0 mx-3 px-4 pt-1 pb-3"
                                variant="underline"
                                activeKey={segmentId}>
                                {bookData?.segments.map((segment) => (
                                    <Nav.Item className="width-fit-content user-select-none">
                                        <Nav.Link href={`#${segment.id}`}
                                                  eventKey={segment.id}>{segment.title}</Nav.Link>
                                    </Nav.Item>
                                ))}
                            </Nav>
                        }
                    </Col></Row>
                }
                <Row>
                    {!mediaProps.mobile &&
                        <Col xs={3}>
                            <h2>Раздели</h2>
                            <Nav className="flex-column segment-navigator m-0"
                                 variant="underline"
                                 activeKey={segmentId}>
                                {bookData?.segments.map((segment, index) => (
                                    <Nav.Item className="width-fit-content user-select-none">
                                        <motion.div {...fadeEffect} key={`segment-nav-${index}`}>
                                            <Nav.Link href={`#${segment.id}`}
                                                      eventKey={segment.id}>{segment.title}</Nav.Link>
                                        </motion.div>
                                    </Nav.Item>
                                ))}
                            </Nav>
                        </Col>
                    }
                    <Col className="p-0">
                        <SegmentViewer
                            bookId={parseInt(book)}
                            onSetSegmentId={setSegmentId}
                            requireAuthCallback={requireAuthCallback}
                            authCheckCatchCallback={authCheckCatchCallback}
                        />
                    </Col>
                </Row>
            </div>
        </motion.div>
    );
};